<script setup lang="ts">
import { ref, VNode } from "vue";
import IconEye from "../icons/IconEye.vue";
import IconEyeCrossed from "../icons/IconEyeCrossed.vue";

// Props
type Props = {
  helpText?: string;
  label?: string;
  name?: string;
  placeholder?: string;
  type: string;
  errors?: string[] | null;
  disabled?: boolean;
  full?: boolean;
  iconPrepend?: VNode;
  iconAppend?: VNode;
  isError?: boolean
  dateFormat?: string;
};

const props = withDefaults(defineProps<Props>(), {
  helpText: "",
  type: "text",
  label: "",
  name: "",
  placeholder: "",
  disabled: false,
  full: false,
  isError: false
});

const showPass = ref(false);

const model = defineModel<string>();

const classes = "inputfield text-base/[38px] w-full"
  + (props.iconPrepend ? " pl-12" : " pl-4")
  + (props.iconAppend ? " pr-12" : " pr-4");
</script>

<template>
  <div>
    <label v-if="label" class="text-gray-500" :for="name">{{ label }}</label>
    <div class="relative">
      <div
        v-if="props.iconPrepend"
        class="absolute inset-y-0 start-0 flex items-center z-20 px-3 text-gray-400 rounded-e-md dark:text-neutral-600"
      >
        <component class="w-6 h-6" :is="props.iconPrepend" />
      </div>

      <input
        :data-date-format="props.dateFormat"
        :id="name"
        v-model.trim="model"
        :aria-describedby="`${name}-help`"
        :name="name"
        :placeholder="placeholder"
        :disabled="disabled"
        :type="showPass ? 'text' : props.type"
        :class="classes"
      />
      <div
        v-if="props.iconAppend"
        class="absolute inset-y-0 end-0 flex items-center z-20 px-3 text-gray-400 rounded-e-md dark:text-neutral-600"
      >
        <component class="w-6 h-6" :is="props.iconAppend" />
      </div>
      <button
        type="button"
        @click="showPass = !showPass"
        v-if="props.type === 'password'"
        class="absolute inset-y-0 end-0 flex items-center z-20 px-3 cursor-pointer text-gray-400 hover:text-gray-300 transition-colors rounded-e-md"
      >
        <IconEye v-if="showPass" class="w-5 h-5" />
        <IconEyeCrossed v-else class="w-5 h-5" />
      </button>
    </div>
    <div class="text-[14px]/[38px] color-red pt-0" v-if="isError">
      {{ errors && errors.length > 0 ? errors[0] : errors }}
    </div>
    <small :id="`${name}-help`">{{ helpText }}</small>
  </div>
</template>

<style lang="scss" scoped>
.inputfield {
  @apply text-lg py-3.5 font-poppins rounded-lg;

  font-size: 16px;
  line-height: 24px;
  background-color: rgba(17, 24, 39, 0.6); /* darker bg-gray-900/60 */
  color: #ffffff;
  border: 1px solid rgba(55, 65, 81, 0.6); /* border-gray-700/60 */
  height: 54px;
  transition: all 0.2s ease;
}

.color-red {
  color: #ef4444; /* red-500 */
}

.field * {
  display: block;
}

label {
  color: #e5e7eb; /* gray-200 */
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 0.5rem;
  display: block;
}

label:disabled {
  color: #9ca3af; /* gray-400 */
}

.inputfield::-moz-placeholder,
.inputfield::placeholder {
  color: #6b7280; /* gray-500 - darker placeholder */
}

.inputfield:hover {
  background: rgba(17, 24, 39, 0.8);
  border-color: rgba(55, 65, 81, 0.8);
}

.inputfield:focus {
  outline: none;
  border-color: #3b82f6; /* blue-500 */
  background: rgba(17, 24, 39, 0.9);
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.inputfield:disabled {
  color: #6b7280; /* gray-500 */
  background: rgba(17, 24, 39, 0.4);
  border-color: rgba(55, 65, 81, 0.4);
}


</style>
